/**
 * Helpers for Shell & modals Appearance UI: map between structured pickers and persisted chrome strings.
 * Avoids free-form CSS in admin; values still resolve through resolveEffectiveChrome when empty.
 */

export type TitleBackgroundPickerMode = 'default' | 'solid' | 'gradient'

export type ChromeBorderPreset = 'default' | 'divider' | 'primaryTint' | 'custom'

export type ActionsBackgroundMode = 'default' | 'primaryTint' | 'solid'

/** Title bar bottom border: divider token or primary tint via CSS variables. */
export const CHROME_TITLE_BORDER_DIVIDER = '1px solid var(--sevv-divider)'
export const CHROME_TITLE_BORDER_PRIMARY_TINT =
  '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)'

/** Actions row top border (matches default resolver spirit at ~20% primary). */
export const CHROME_ACTIONS_BORDER_DIVIDER = '1px solid var(--sevv-divider)'
export const CHROME_ACTIONS_BORDER_PRIMARY_TINT =
  '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent)'

function expandHex3(h: string): string {
  if (h.length !== 3) return h
  return h[0] + h[0] + h[1] + h[1] + h[2] + h[2]
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const h = hex.replace('#', '').trim()
  const full = h.length === 3 ? expandHex3(h) : h
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  }
}

export function rgbToHex(r: number, g: number, b: number): string {
  const x = (n: number) => n.toString(16).padStart(2, '0')
  return `#${x(r)}${x(g)}${x(b)}`
}

export function formatBackdropRgba(hex: string, opacity: number): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return 'rgba(0, 0, 0, 0.5)'
  const a = Math.min(1, Math.max(0, opacity))
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`
}

export function parseBackdropRgba(raw: string | undefined): { hex: string; opacity: number } {
  if (!raw?.trim()) return { hex: '#000000', opacity: 0.5 }
  const m = raw.trim().match(
    /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/i
  )
  if (!m) return { hex: '#000000', opacity: 0.5 }
  const r = Number(m[1])
  const g = Number(m[2])
  const b = Number(m[3])
  const a = m[4] !== undefined ? Number(m[4]) : 1
  return { hex: rgbToHex(r, g, b), opacity: Number.isFinite(a) ? a : 0.5 }
}

const GRADIENT_135 = /linear-gradient\s*\(\s*135deg\s*,\s*([^,]+)\s*0%\s*,\s*([^,]+)\s*100%\s*\)/i

export function parseTitleBackground(
  raw: string | undefined,
  palette: { primary: string; primaryLight: string }
): { mode: TitleBackgroundPickerMode; solid: string; gFrom: string; gTo: string } {
  const t = raw?.trim() ?? ''
  if (!t) {
    return { mode: 'default', solid: palette.primary, gFrom: palette.primary, gTo: palette.primaryLight }
  }
  if (/^#[0-9a-fA-F]{6}$/.test(t) || /^#[0-9a-fA-F]{3}$/.test(t)) {
    const norm = t.length === 4 ? `#${expandHex3(t.slice(1))}` : t
    return { mode: 'solid', solid: norm, gFrom: palette.primary, gTo: palette.primaryLight }
  }
  const gm = t.match(GRADIENT_135)
  if (gm) {
    const from = gm[1].trim()
    const to = gm[2].trim()
    const hf = /^#[0-9a-fA-F]{3,8}$/.test(from)
    const ht = /^#[0-9a-fA-F]{3,8}$/.test(to)
    if (hf && ht) {
      return {
        mode: 'gradient',
        solid: palette.primary,
        gFrom: from.length === 4 ? `#${expandHex3(from.slice(1))}` : from.slice(0, 7),
        gTo: to.length === 4 ? `#${expandHex3(to.slice(1))}` : to.slice(0, 7),
      }
    }
  }
  return { mode: 'gradient', solid: palette.primary, gFrom: palette.primary, gTo: palette.primaryLight }
}

export function buildTitleBackground(
  mode: TitleBackgroundPickerMode,
  solid: string,
  gFrom: string,
  gTo: string
): string {
  if (mode === 'default') return ''
  if (mode === 'solid') {
    const s = solid.trim()
    return /^#[0-9a-fA-F]{6}$/i.test(s) ? s : ''
  }
  const a = gFrom.trim()
  const b = gTo.trim()
  if (!/^#[0-9a-fA-F]{6}$/i.test(a) || !/^#[0-9a-fA-F]{6}$/i.test(b)) return ''
  return `linear-gradient(135deg, ${a} 0%, ${b} 100%)`
}

export function detectTitleBorderPreset(raw: string | undefined): ChromeBorderPreset {
  const t = raw?.trim() ?? ''
  if (!t) return 'default'
  if (t.includes('var(--sevv-divider)')) return 'divider'
  if (t.includes('var(--sevv-primary') && t.includes('color-mix')) return 'primaryTint'
  return 'custom'
}

export function detectActionsBorderPreset(raw: string | undefined): ChromeBorderPreset {
  return detectTitleBorderPreset(raw)
}

export function parseCustomBorder(raw: string | undefined): { width: number; color: string } {
  if (!raw?.trim()) return { width: 1, color: '#eeeeee' }
  const m = raw.trim().match(/^(\d+)px\s+solid\s+(#\w{3,8})$/i)
  if (!m) return { width: 1, color: '#cccccc' }
  const w = Math.min(4, Math.max(1, parseInt(m[1], 10)))
  let color = m[2]
  if (/^#[0-9a-fA-F]{3}$/i.test(color)) {
    color = `#${expandHex3(color.slice(1))}`
  }
  if (color.length > 7) color = color.slice(0, 7)
  return { width: w, color }
}

export function buildChromeBorder(
  preset: ChromeBorderPreset,
  customWidth: number,
  customColor: string,
  primaryTintTemplate: 'title' | 'actions'
): string {
  if (preset === 'default') return ''
  if (preset === 'divider') {
    return primaryTintTemplate === 'title' ? CHROME_TITLE_BORDER_DIVIDER : CHROME_ACTIONS_BORDER_DIVIDER
  }
  if (preset === 'primaryTint') {
    return primaryTintTemplate === 'title' ? CHROME_TITLE_BORDER_PRIMARY_TINT : CHROME_ACTIONS_BORDER_PRIMARY_TINT
  }
  const w = Math.min(4, Math.max(1, customWidth))
  const c = customColor.trim()
  if (!/^#[0-9a-fA-F]{6}$/i.test(c)) return ''
  return `${w}px solid ${c}`
}

export function detectActionsBackgroundMode(
  raw: string | undefined,
  palettePrimary: string
): { mode: ActionsBackgroundMode; opacity: number; solid: string } {
  const t = raw?.trim() ?? ''
  if (!t) return { mode: 'default', opacity: 0.08, solid: '#ffffff' }
  const rgb = hexToRgb(palettePrimary)
  if (rgb) {
    const prefix = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, `
    if (t.startsWith(prefix) && t.endsWith(')')) {
      const inner = t.slice(prefix.length, -1).trim()
      const a = parseFloat(inner)
      if (Number.isFinite(a)) return { mode: 'primaryTint', opacity: a, solid: '#ffffff' }
    }
  }
  if (/^#[0-9a-fA-F]{6}$/i.test(t)) return { mode: 'solid', opacity: 0.08, solid: t }
  return { mode: 'solid', opacity: 0.08, solid: '#f5f5f5' }
}

export function buildActionsBackground(
  mode: ActionsBackgroundMode,
  primaryHex: string,
  tintOpacity: number,
  solidHex: string
): string {
  if (mode === 'default') return ''
  if (mode === 'primaryTint') {
    const rgb = hexToRgb(primaryHex)
    if (!rgb) return ''
    const a = Math.min(0.35, Math.max(0.02, tintOpacity))
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`
  }
  const s = solidHex.trim()
  return /^#[0-9a-fA-F]{6}$/i.test(s) ? s : ''
}
